import type { BookResult, LabelDoc } from '../types';
import { money, shortDate } from '../lib/format';

/**
 * Renders a carrier label document exactly as RateLinx returned it.
 *
 * Only PNG and PDF are inlined. ZPL comes back as raw label language, which is
 * for a Zebra printer rather than a screen - RateLinx exposes
 * Engine/ConvertZplToBase64Image/v1 if a preview is ever needed.
 */
function LabelView({ doc }: { doc: LabelDoc }) {
  const format = doc.format.toUpperCase();

  if (!doc.contentBase64) return null;

  if (format.includes('PNG') || format.includes('IMAGE')) {
    return (
      <div className="label-doc">
        <div className="sub-note" style={{ marginBottom: 6 }}>
          {doc.docType} · {doc.format}
        </div>
        <img src={`data:image/png;base64,${doc.contentBase64}`} alt={`Carrier label ${doc.index + 1}`} />
      </div>
    );
  }

  if (format.includes('PDF')) {
    return (
      <div className="label-doc">
        <div className="sub-note" style={{ marginBottom: 6 }}>
          {doc.docType} · {doc.format}
        </div>
        <object
          data={`data:application/pdf;base64,${doc.contentBase64}`}
          type="application/pdf"
          width="100%"
          height="460"
          aria-label={`Carrier label ${doc.index + 1}`}
        >
          <a href={`data:application/pdf;base64,${doc.contentBase64}`} download={`label-${doc.index + 1}.pdf`}>
            Download label PDF
          </a>
        </object>
      </div>
    );
  }

  return (
    <div className="label-doc">
      <div className="sub-note">
        {doc.docType} · {doc.format} · {Math.round((doc.contentBase64.length * 3) / 4 / 1024)} KB
      </div>
      <div style={{ marginTop: 6, fontSize: 12 }}>
        Returned in a printer format rather than an image. Send it straight to the label printer.
      </div>
    </div>
  );
}

export function BookingPanel({ result, onClear }: { result: BookResult; onClear: () => void }) {
  const { booking, rateSource } = result;
  const simulated = rateSource !== 'live';

  return (
    <div className="panel">
      <div className="panel-head">
        <div>
          <h2>{simulated ? 'Booking simulated' : 'Shipment booked'}</h2>
          <div className="hint">
            {booking.serviceName} · {booking.carrier || 'FedEx'}
          </div>
        </div>
        <button type="button" className="btn small" onClick={onClear}>
          Clear
        </button>
      </div>

      <div className="panel-body">
        {simulated && (
          <div className="callout warn">
            <span className="icon" aria-hidden="true">
              !
            </span>
            <span>
              Fixture mode — nothing was tendered to a carrier. No label is produced here by design;
              a carrier label is a real shipping record, so this kit does not fabricate one. Point
              the relay at a live token to book through <code>Engine/Ship/v1</code> and get the real
              label back.
            </span>
          </div>
        )}

        <div className="booking-grid">
          <div className="field">
            <div className="label">Tracking</div>
            <div className="value mono">{booking.trackingNumber || '—'}</div>
          </div>
          <div className="field">
            <div className="label">Shipment ID</div>
            <div className="value mono">{booking.shipmentId || '—'}</div>
          </div>
          {booking.bolNumber && (
            <div className="field">
              <div className="label">BOL</div>
              <div className="value mono">{booking.bolNumber}</div>
            </div>
          )}
          {booking.proNumber && (
            <div className="field">
              <div className="label">PRO</div>
              <div className="value mono">{booking.proNumber}</div>
            </div>
          )}
          <div className="field">
            <div className="label">Freight cost</div>
            <div className="value">{money(booking.charge)}</div>
          </div>
          <div className="field">
            <div className="label">Booked</div>
            <div className="value">{shortDate(booking.bookedAt.slice(0, 10))}</div>
          </div>
        </div>

        {booking.labels.length > 0 ? (
          booking.labels.map((doc) => <LabelView key={doc.index} doc={doc} />)
        ) : !simulated ? (
          <div className="callout">
            <span className="icon" aria-hidden="true">
              i
            </span>
            <span>
              The carrier returned no label document on this response. Labels can also be pulled
              separately from <code>Engine/ShipmentLabels/v1</code> using the shipment ID.
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}
